import { useRef, useState } from 'react';

import posthog from 'posthog-js';

import useExtractPartyFromEmail from '@/features/analysis/mutations/useExtractPartyFromEmail';
import useUploadEml from '@/features/analysis/mutations/useUploadEml';
import useCreateDocumentForDistribution from '@/features/documents/mutations/useCreateDocumentForDistribution';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import OfficeAsyncError from '@/features/email/outlook/OfficeAsyncError';
import type { EmailMessage } from '@/features/email/types';
import usePollSingularityJob from '@/hooks/usePollSingularityJob';
import { buildEmlFilename } from '@/lib/email/buildEmlFilename';
import type { DocumentExtractorPartyOneResult } from '@/types/singularity';

export default function usePartyExtraction(email: Maybe<EmailMessage>) {
  const [isExtractingParty, setIsExtractingParty] = useState(false);
  const [extractedParty, setExtractedParty] = useState<
    | (DocumentExtractorPartyOneResult['party'] & { messageId: string })
    | undefined
  >(undefined);
  const hasStartedFlow = useRef<string | boolean>(false);

  const { getEmlBlob } = useEmailClient();

  const createDocumentForDistributionMutation =
    useCreateDocumentForDistribution();
  const uploadEmlMutation = useUploadEml();
  const extractPartyMutation = useExtractPartyFromEmail();
  const { executeJob } = usePollSingularityJob();

  const createDocumentForDistribution = async () => {
    if (!email)
      throw new Error('No email data when trying to create document for party');

    const filename = buildEmlFilename(email?.subject);
    const description = email?.messageId;

    const result = await createDocumentForDistributionMutation.mutateAsync({
      filename,
      description,
      isHiddenForCustomer: true,
    });

    return {
      documentId: result.document_id,
      uploadLink: result.link,
      publicUrl: result.public_url,
      filename,
    };
  };

  const startPartyExtractionFlow = async () => {
    if (!email) return;
    if (email.messageId === extractedParty?.messageId) return;
    if (hasStartedFlow.current === email.messageId) return;

    setIsExtractingParty(true);
    hasStartedFlow.current = email.messageId;
    const startedAt = performance.now();
    posthog.capture('Started party extraction', {
      messageId: email.messageId,
    });

    try {
      const { documentId, uploadLink, publicUrl, filename } =
        await createDocumentForDistribution();

      const eml = await getEmlBlob();

      await uploadEmlMutation.mutateAsync({ link: uploadLink, blob: eml });

      const extractResult = await extractPartyMutation.mutateAsync({
        documentId,
        link: publicUrl,
        filename,
      });

      const result = await executeJob<DocumentExtractorPartyOneResult>(
        extractResult.id,
      );

      setExtractedParty({
        ...result.result.party,
        messageId: email.messageId,
      });

      posthog.capture('Completed party extraction', {
        messageId: email.messageId,
        matched: Boolean(result.result.party),
        durationMs: Math.round(performance.now() - startedAt),
      });
    } catch (error) {
      posthog.capture('Party extraction failed', {
        isFailure: true,
        messageId: email.messageId,
        durationMs: Math.round(performance.now() - startedAt),
        errorMessage: error instanceof Error ? error.message : String(error),
        ...(error instanceof OfficeAsyncError && {
          officeErrorCode: error.officeErrorCode,
          officeErrorName: error.officeErrorName,
        }),
      });
    } finally {
      setIsExtractingParty(false);
    }
  };

  return {
    extractedParty,
    isExtractingParty,
    startPartyExtractionFlow,
  };
}
